// NOTICE: This file was copied from the oppizi-shared repository, do
// not modify it here.

import { InvalidRequest } from './error'

export const INVENTORY_MOVEMENT_OUT = 2
export const INVENTORY_MOVEMENT_IN = 1
export const INVENTORY_MOVEMENT_INIT = 0
export const INVENTORY_MOVEMENT_DISTRIBUTED = 3

/**
 * This processes a list of inventory items record history into the current
 * state of inventory items.
 * e.g. you may have two hats with movement type of 'init' and then a record
 * of 2 hats with a movement type of 'out'. This would amalgamate those two
 * records and return 0 hats for 'in' and 2 hats for 'out'. Further out
 * records are distributed among their 'related user's. So if the two hats
 * were given to two different brand ambassadors this function would return
 * two inventory records for those two brand ambassadors.
 * Always returns records with a movement of either 'in' or 'out'. 'init'
 * is not represented.
 * An InventoryMovement object has the following structure
 * {
 *   movementTypeId: 1,
 *   relatedUserId: 2,
 *   quantity: 500,
 *   inventoryId: 4,
 *   relatedCityId: 5
 * }
 * @param {Array<InventoryMovement>} history
 * @returns {Array<InventoryMovement>}
 */
function summariseInventoryMovements(
  history,
  opt_allowNegative = true,
  strict = false,
  opt_removeZeroRecords = true,
) {
  var newItems = []

  // First sort into the same city
  groupByCity(history).forEach(history => {
    var tallyInStorage = 0

    var filteredHistory = []
    history.forEach(item => {
      try {
        validateInventoryHistoryItem(item)
      } catch (err) {
        /*eslint-disable */
        if (strict) throw err
        console.warn(err)
        console.warn(
          'This inventory item was skipped because it was not valid.',
        )
        /*eslint-enable */
        return
      }

      filteredHistory.push(normaliseHistoryItem(item))
    })

    groupByAmbassador(filteredHistory).forEach(history => {
      // Dealing with movemnts for the same BA for the same city
      var where = { ba: 0, distributed: 0 }

      // Start with calculating the totals
      history.forEach(item => {
        // If the items have no ambassadors assume this is our INIT group of
        // records
        if (item.relatedUserId == null) {
          tallyInStorage += item.quantity
          return
        }

        switch (item.movementTypeId) {
          case INVENTORY_MOVEMENT_IN:
            tallyInStorage += item.quantity
            // Also keep track of individual BAs
            where.ba -= item.quantity
            break
          case INVENTORY_MOVEMENT_OUT:
            tallyInStorage -= item.quantity
            // Also keep track of individual BAs
            where.ba += item.quantity
            break
          case INVENTORY_MOVEMENT_DISTRIBUTED:
            // When the BA distributes the flyers
            where.ba -= item.quantity
            where.distributed += item.quantity
            break
        }
      })

      if (opt_allowNegative === false) {
        if (where.ba < 0)
          throw new InvalidRequest(
            'Cannot return or distribute more flyers than the BA has.',
          )
      }

      // If this is the group without relatedUserIds assume its our INIT group
      // so skip adding records
      if (history[0].relatedUserId == null) return

      if (history[0]) {
        if (opt_removeZeroRecords === false || where.ba != 0)
          newItems.push({
            movementTypeId: INVENTORY_MOVEMENT_OUT,
            relatedUserId: history[0].relatedUserId,
            quantity: where.ba,
            inventoryId: history[0].inventoryId,
            relatedCityId: history[0].relatedCityId,
          })
        if (opt_removeZeroRecords === false || where.distributed != 0)
          newItems.push({
            movementTypeId: INVENTORY_MOVEMENT_DISTRIBUTED,
            relatedUserId: history[0].relatedUserId,
            quantity: where.distributed,
            inventoryId: history[0].inventoryId,
            relatedCityId: history[0].relatedCityId,
          })
      }
    })

    if (opt_allowNegative === false) {
      if (tallyInStorage < 0)
        throw new InvalidRequest('Not enough inventory in stock.')
    }

    if (history[0]) {
      if (opt_removeZeroRecords === false || tallyInStorage != 0)
        newItems.splice(0, 0, {
          movementTypeId: INVENTORY_MOVEMENT_IN,
          relatedUserId: null,
          quantity: tallyInStorage,
          inventoryId: history[0].inventoryId,
          relatedCityId: history[0].relatedCityId,
        })
    }
  })

  return newItems

  /**
   * Return an array of arrays where containing arrays are grouped by the city
   * @param {Array<InventoryMovement>}
   * @returns {Array<Array<InventoryMovement>>}
   */
  function groupByCity(items) {
    return groupBy(items, 'relatedCityId')
  }

  /**
   * Return an array of arrays where containing arrays are grouped by the BA
   * @param {Array<InventoryMovement>}
   * @returns {Array<Array<InventoryMovement>>}
   */
  function groupByAmbassador(items) {
    return groupBy(items, 'relatedUserId')
  }

  /**
   * Return an array of arrays where containing arrays are grouped by
   * the provided prop.
   * @param {Array<object>}
   * @returns {Array<Array<object>>}
   */
  function groupBy(items, prop) {
    var pivot = {}
    items.forEach(item => {
      var groupByVal = item[prop]
      pivot[groupByVal] = pivot[groupByVal] || []
      pivot[groupByVal].push(item)
    })
    return Object.values(pivot)
  }
}

/**
 * @deprecated Use summariseInventoryMovements
 */
function processInventoryMovements() {
  return summariseInventoryMovements(...arguments)
}

/**
 * Returns if two inventory items are the same, in the sense of matching
 * item type, size, client and city.
 * @param {Inventory} a
 * @param {Inventory} b
 * @returns {boolean}
 */
function inventoryEqualityCheck(a, b) {
  return a.inventoryId == b.inventoryId && a.relatedCityId == b.relatedCityId
}

/**
 * Throws an error if the inventory history item is invalid.
 * @param {Inventory} item
 */
function validateInventoryHistoryItem(item) {
  if (item.movementTypeId == 0 && item.relatedUserId) {
    throw new InvalidRequest(
      `Inventory item can only have a related user if the
      movement is 'OUT' or 'IN'.`.replace(/\s+/g, ' '),
    )
  }
  if (item.movementTypeId === 2 && !item.relatedUserId)
    throw new InvalidRequest(
      `Inventory item must have a related user if the
      movement is 'OUT'.`.replace(/\s+/g, ' '),
    )
  if (!item.relatedCityId)
    throw new InvalidRequest('Inventory item must have a related city.')
}

/**
 * Ensures the history item has valid props.
 */
function normaliseHistoryItem(item) {
  // Ensure if we're sent 0 for the user id it's normalised to null
  if (item.relatedUserId === 0)
    item = { ...item, relatedUserId: null }
    // eslint-disable-next-line no-extra-semi
  ;['movementTypeId', 'quantity'].forEach(prop => {
    if (typeof item[prop] === 'string') {
      var val = Number(item[prop])
      if (val != item[prop])
        throw new InvalidRequest(
          `History item has non numeric value for ${prop}.`,
        )
      item = { ...item }
      item[prop] = val
    }
  })
  return item
}

/**
 * Figures out how much of your inventory should be either set to 'init', 'in'
 * or 'out' based on a movement of quantity. Will throw an error if its an
 * invalid movement, for example if there's only 4 items either in or 'init'.
 * And you try and move 5 items 'out'. Optionally set opt_allowNegative to true
 * to stop this method throwing on a negative inventory
 */
const applyMovementToInventoryTotals = (
  totals,
  movement,
  quantity,
  opt_allowNegative = false,
) => {
  var newTotals = Object.assign({}, totals)

  switch (movement) {
    case INVENTORY_MOVEMENT_INIT:
    case INVENTORY_MOVEMENT_INIT + '': {
      newTotals.init += quantity
      break
    }
    case INVENTORY_MOVEMENT_IN:
    case INVENTORY_MOVEMENT_IN + '': {
      if (!opt_allowNegative && quantity > totals.out)
        throw new InvalidRequest(
          `Can't move ${quantity} items in when only ${totals.out} are out.`,
        )
      newTotals.in += quantity
      newTotals.out -= quantity
      break
    }
    case INVENTORY_MOVEMENT_OUT:
    case INVENTORY_MOVEMENT_OUT + '': {
      if (!opt_allowNegative && quantity > totals.init + totals.in)
        throw new InvalidRequest(
          `Not enough inventory to move ${quantity} items out when there's ${
            totals.init
          } as 'init' and ${totals.in} as 'in'.`,
        )
      let initQuantity = Math.min(totals.init, quantity),
        inQuantity = quantity - initQuantity
      newTotals.init = newTotals.init - initQuantity
      newTotals.in = newTotals.in - inQuantity
      newTotals.out += quantity
      break
    }
    case INVENTORY_MOVEMENT_DISTRIBUTED:
    case INVENTORY_MOVEMENT_DISTRIBUTED + '': {
      if (!opt_allowNegative && quantity > totals.out)
        throw new InvalidRequest(
          `Not enough inventory out to then distribute ${quantity} items when there's ${
            totals.out
          } as 'out'.`,
        )
      newTotals.out -= quantity
      newTotals.distributed += quantity
      break
    }
  }

  return newTotals
}

export {
  processInventoryMovements,
  summariseInventoryMovements,
  applyMovementToInventoryTotals,
  inventoryEqualityCheck,
  validateInventoryHistoryItem,
}
